/**
 * v4.34.13 — Navigation Layout preference context.
 *
 * Mirrors `ThemeContext` exactly: localStorage-backed, client-only,
 * no backend round-trip. Persists across logout/login on the same
 * device, identical to how Theme and Language preferences behave.
 *
 * Values:
 *   'sidebar' — traditional left-side navigation (default)
 *   'top'     — alternative top-bar navigation
 *
 * Mobile (< 1024 px) always uses the sidebar drawer regardless of
 * preference. The toggle UI hides itself on mobile so the option
 * doesn't silently fail.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'yulontap-nav-layout';
const VALID_VALUES = new Set(['sidebar', 'top']);
const DEFAULT_VALUE = 'sidebar';

const NavLayoutContext = createContext(null);

function readInitial() {
    if (typeof window === 'undefined') return DEFAULT_VALUE;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && VALID_VALUES.has(stored)) return stored;
    } catch { /* localStorage unavailable */ }
    return DEFAULT_VALUE;
}

export const NavLayoutProvider = ({ children }) => {
    const [navLayout, setNavLayoutState] = useState(readInitial);

    // Persist on change
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, navLayout); }
        catch { /* localStorage unavailable */ }
    }, [navLayout]);

    const setNavLayout = useCallback((value) => {
        if (!VALID_VALUES.has(value)) return;
        setNavLayoutState(value);
    }, []);

    const toggleNavLayout = useCallback(() => {
        setNavLayoutState((prev) => (prev === 'top' ? 'sidebar' : 'top'));
    }, []);

    return (
        <NavLayoutContext.Provider value={{ navLayout, setNavLayout, toggleNavLayout }}>
            {children}
        </NavLayoutContext.Provider>
    );
};

export const useNavLayout = () => {
    const ctx = useContext(NavLayoutContext);
    if (!ctx) {
        // Safe fallback for tree positions outside the provider
        // (e.g., during initial bootstrap before App mounts).
        return { navLayout: DEFAULT_VALUE, setNavLayout: () => {}, toggleNavLayout: () => {} };
    }
    return ctx;
};
